import React, { useMemo } from 'react';
import { useCalculatorStore } from '../../store/calculatorStore';
import type { ProvinceCalculatorId } from '../../config/provinceCalculatorPages';
import { formatUsd } from '../../utils/articleScenarioMetrics';
import { ontarioClosingCostEstimates } from '../../utils/ontarioLandTransferTax';
import { bcClosingCostEstimates } from '../../utils/bcPropertyTransferTax';
import { albertaClosingCostEstimates } from '../../utils/albertaLandTitleTransferFee';

interface ProvinceClosingCostsBreakdownProps {
  provinceId: ProvinceCalculatorId;
  tabIsToronto?: Record<string, boolean>;
}

function getCmhcPremium(homePrice: number, downPayment: number, premiumRate: number): number {
  const baseLoan = homePrice - downPayment;
  return Math.round(baseLoan * premiumRate);
}

export const ProvinceClosingCostsBreakdown: React.FC<ProvinceClosingCostsBreakdownProps> = ({
  provinceId,
  tabIsToronto = {},
}) => {
  const calculatorTabs = useCalculatorStore((s) => s.calculatorTabs);
  const activeTabId = useCalculatorStore((s) => s.activeTabId);

  const activeTab = useMemo(() => {
    if (activeTabId === 'compare') return calculatorTabs[0] ?? null;
    return calculatorTabs.find((t) => t.id === activeTabId) ?? calculatorTabs[0] ?? null;
  }, [calculatorTabs, activeTabId]);

  const breakdown = useMemo(() => {
    if (!activeTab) return null;
    const terms = activeTab.data.mortgageTerms;
    const price = terms.homePrice;
    const down = terms.downPaymentAmount;
    const cmhcPremium =
      terms.loanProgram === 'cmhc' && terms.cmhcDetails
        ? getCmhcPremium(price, down, terms.cmhcDetails.premiumRate)
        : 0;

    if (provinceId === 'ontario') {
      const isToronto = tabIsToronto[activeTab.id] ?? false;
      return {
        type: 'ontario' as const,
        est: ontarioClosingCostEstimates({ purchasePrice: price, cmhcPremium, isToronto }),
        isToronto,
        price,
        down,
        cmhcPremium,
      };
    }
    if (provinceId === 'bc') {
      return {
        type: 'bc' as const,
        est: bcClosingCostEstimates({ purchasePrice: price, cmhcPremium }),
        price,
        down,
        cmhcPremium,
      };
    }
    return {
      type: 'alberta' as const,
      est: albertaClosingCostEstimates({ purchasePrice: price, cmhcPremium }),
      price,
      down,
      cmhcPremium,
    };
  }, [activeTab, provinceId, tabIsToronto]);

  if (!breakdown || !activeTab) return null;

  const fmt = formatUsd;

  if (breakdown.type === 'ontario') {
    const { est, isToronto } = breakdown;
    const transferLabel = isToronto ? 'Combined land transfer tax (provincial + Toronto)' : 'Provincial land transfer tax';
    const fthbTransfer = isToronto
      ? est.netProvincialLtt + est.netTorontoMltt
      : est.netProvincialLtt;
    const repeatTransfer = isToronto
      ? est.provincialLttGross + est.municipalLttGross
      : est.provincialLttGross;

    return (
      <section
        aria-labelledby="province-closing-costs-heading"
        className="px-6 py-6 border-t border-stone-border/80 bg-stone-50/50"
      >
        <h2 id="province-closing-costs-heading" className="font-serif text-xl text-ink mb-1">
          Closing cost estimate for {activeTab.title}
        </h2>
        <p className="text-sm text-ink-muted mb-4">
          Cash due at closing beyond your down payment of {fmt(breakdown.down)} on {fmt(breakdown.price)}.
          {breakdown.cmhcPremium > 0
            ? ` CMHC premium of ${fmt(breakdown.cmhcPremium)} is rolled into the mortgage, not paid in cash.`
            : ' No CMHC premium on this tab.'}
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-stone-200 rounded-lg bg-white">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-ink border-b border-stone-200">Line item</th>
                <th className="px-4 py-2 text-left font-semibold text-ink border-b border-stone-200">First-time buyer</th>
                <th className="px-4 py-2 text-left font-semibold text-ink border-b border-stone-200">Repeat buyer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {[
                [transferLabel, fmt(fthbTransfer), fmt(repeatTransfer)],
                ['Ontario PST on CMHC premium (8%)', fmt(est.cmhcPremiumPst), fmt(est.cmhcPremiumPst)],
                ['Legal fees', fmt(est.legalFees), fmt(est.legalFees)],
                ['Title insurance', fmt(est.titleInsurance), fmt(est.titleInsurance)],
                ['Home inspection', fmt(est.homeInspection), fmt(est.homeInspection)],
                ['Closing adjustments', fmt(est.closingAdjustments), fmt(est.closingAdjustments)],
                [
                  'Total beyond down payment',
                  fmt(est.totalBeyondDownPaymentFthb),
                  fmt(est.totalBeyondDownPaymentRepeat),
                ],
              ].map(([label, fthb, repeat]) => (
                <tr key={label} className="even:bg-stone-50/50">
                  <td className="px-4 py-2 font-medium text-ink">{label}</td>
                  <td className="px-4 py-2 text-ink-muted">{fthb}</td>
                  <td className="px-4 py-2 text-ink-muted">{repeat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  if (breakdown.type === 'bc') {
    const { est } = breakdown;
    return (
      <section
        aria-labelledby="province-closing-costs-heading"
        className="px-6 py-6 border-t border-stone-border/80 bg-stone-50/50"
      >
        <h2 id="province-closing-costs-heading" className="font-serif text-xl text-ink mb-1">
          Closing cost estimate for {activeTab.title}
        </h2>
        <p className="text-sm text-ink-muted mb-4">
          Cash due at closing beyond your down payment of {fmt(breakdown.down)} on {fmt(breakdown.price)}.
          CMHC premium of {fmt(breakdown.cmhcPremium)} is rolled into the mortgage.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-stone-200 rounded-lg bg-white">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-ink border-b border-stone-200">Line item</th>
                <th className="px-4 py-2 text-left font-semibold text-ink border-b border-stone-200">First-time buyer</th>
                <th className="px-4 py-2 text-left font-semibold text-ink border-b border-stone-200">Repeat buyer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {[
                ['Property transfer tax (PTT)', fmt(est.netPttFthb), fmt(est.netPttRepeat)],
                ['BC PST on CMHC premium (7%)', fmt(est.cmhcPremiumPst), fmt(est.cmhcPremiumPst)],
                ['Legal fees', fmt(est.legalFees), fmt(est.legalFees)],
                ['Title insurance', fmt(est.titleInsurance), fmt(est.titleInsurance)],
                ['Home inspection', fmt(est.homeInspection), fmt(est.homeInspection)],
                ['Closing adjustments', fmt(est.closingAdjustments), fmt(est.closingAdjustments)],
                [
                  'Total beyond down payment',
                  fmt(est.totalBeyondDownPaymentFthb),
                  fmt(est.totalBeyondDownPaymentRepeat),
                ],
              ].map(([label, fthb, repeat]) => (
                <tr key={label} className="even:bg-stone-50/50">
                  <td className="px-4 py-2 font-medium text-ink">{label}</td>
                  <td className="px-4 py-2 text-ink-muted">{fthb}</td>
                  <td className="px-4 py-2 text-ink-muted">{repeat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  const { est } = breakdown;
  return (
    <section
      aria-labelledby="province-closing-costs-heading"
      className="px-6 py-6 border-t border-stone-border/80 bg-stone-50/50"
    >
      <h2 id="province-closing-costs-heading" className="font-serif text-xl text-ink mb-1">
        Closing cost estimate for {activeTab.title}
      </h2>
      <p className="text-sm text-ink-muted mb-4">
        Cash due at closing beyond your down payment of {fmt(breakdown.down)} on {fmt(breakdown.price)}.
        Alberta has no land transfer tax. Ontario provincial LTT on the same price would be{' '}
        {fmt(est.ontarioProvincialLttComparison)} ({fmt(est.lttSavingsVsOntario)} saved here).
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-stone-200 rounded-lg bg-white">
          <thead className="bg-stone-50">
            <tr>
              <th className="px-4 py-2 text-left font-semibold text-ink border-b border-stone-200">Line item</th>
              <th className="px-4 py-2 text-left font-semibold text-ink border-b border-stone-200">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {[
              ['Land title registration fee', fmt(est.landTitleFee)],
              ['Ontario LTT comparison (same price)', fmt(est.ontarioProvincialLttComparison)],
              ['Toronto combined LTT comparison', fmt(est.ontarioTorontoCombinedLttComparison)],
              ['CMHC premium PST', fmt(est.cmhcPremiumPst)],
              ['Legal fees', fmt(est.legalFees)],
              ['Title insurance', fmt(est.titleInsurance)],
              ['Home inspection', fmt(est.homeInspection)],
              ['Closing adjustments', fmt(est.closingAdjustments)],
              ['Total beyond down payment', fmt(est.totalBeyondDownPaymentFthb)],
            ].map(([label, amount]) => (
              <tr key={label} className="even:bg-stone-50/50">
                <td className="px-4 py-2 font-medium text-ink">{label}</td>
                <td className="px-4 py-2 text-ink-muted">{amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
